/**
 * Announcement link + platform helpers.
 *
 * Shared by the admin form (inline validation), the admin API routes
 * (authoritative server-side validation — the client is never trusted)
 * and the public banner, so all three agree on exactly what a valid
 * click-through target and platform value look like.
 */

/** Where an announcement renders. 'desktop' = this website, 'mobile' =
 *  the React Native app, 'both' = everywhere. */
export const ANNOUNCEMENT_PLATFORMS = ['both', 'desktop', 'mobile'] as const
export type AnnouncementPlatform = (typeof ANNOUNCEMENT_PLATFORMS)[number]

/** Platform values this website renders a banner for. */
export const WEB_ANNOUNCEMENT_PLATFORMS: AnnouncementPlatform[] = ['desktop', 'both']

export function isAnnouncementPlatform(value: unknown): value is AnnouncementPlatform {
  return (
    typeof value === 'string' &&
    (ANNOUNCEMENT_PLATFORMS as readonly string[]).includes(value)
  )
}

/** Single copy of the validation message so the inline admin error and
 *  the API's 400 body read identically. */
export const ANNOUNCEMENT_LINK_ERROR =
  'Link URL must be an absolute http(s) URL or a site path starting with "/"'

export const ANNOUNCEMENT_PLATFORM_ERROR =
  "platform must be one of 'both', 'desktop', 'mobile'"

/**
 * Accepts only an absolute http(s) URL or a site-relative path.
 *
 * Everything else is rejected — notably `javascript:` and `data:` URIs,
 * which `new URL()` parses happily, so the protocol allow-list (not a
 * deny-list) is what does the work. Protocol-relative `//evil.com` and
 * its backslash variants are rejected too: they start with '/' and read
 * as internal but navigate off-site.
 */
export function isValidAnnouncementLink(value: string): boolean {
  const trimmed = value.trim()
  if (!trimmed) return false

  if (trimmed.startsWith('/')) return !/^\/[/\\]/.test(trimmed)

  let parsed: URL
  try {
    parsed = new URL(trimmed)
  } catch {
    return false
  }
  return parsed.protocol === 'http:' || parsed.protocol === 'https:'
}

/** True when the link points off-site and must open in a new tab. */
export function isExternalAnnouncementLink(value: string): boolean {
  return !value.trim().startsWith('/')
}

// ---------------------------------------------------------------------------
// Targeting, scheduling + severity (added with announcement targeting)
// ---------------------------------------------------------------------------

/** Severity drives the banner's default colors when no explicit hex override is set. */
export const ANNOUNCEMENT_SEVERITIES = ['info', 'success', 'warning', 'critical'] as const
export type AnnouncementSeverity = (typeof ANNOUNCEMENT_SEVERITIES)[number]

export function isAnnouncementSeverity(value: unknown): value is AnnouncementSeverity {
  return typeof value === 'string' && (ANNOUNCEMENT_SEVERITIES as readonly string[]).includes(value)
}

export const ANNOUNCEMENT_SEVERITY_ERROR =
  "severity must be one of 'info', 'success', 'warning', 'critical'"

/**
 * Default banner colors per severity, applied ONLY when the row sets no
 * explicit `background_color` override AND severity is not 'info' (info keeps
 * the existing brand-blue class so pre-targeting rows look identical).
 */
export const ANNOUNCEMENT_SEVERITY_COLORS: Record<
  AnnouncementSeverity,
  { bg: string; text: string }
> = {
  info: { bg: '#00059f', text: '#ffffff' }, // brand blue (current default)
  success: { bg: '#166534', text: '#ffffff' }, // green-800
  warning: { bg: '#b45309', text: '#ffffff' }, // amber-700
  critical: { bg: '#991b1b', text: '#ffffff' }, // red-800
}

/** Who sees an announcement, by auth state. */
export const ANNOUNCEMENT_AUDIENCES = ['all', 'members', 'guests'] as const
export type AnnouncementAudience = (typeof ANNOUNCEMENT_AUDIENCES)[number]

export function isAnnouncementAudience(value: unknown): value is AnnouncementAudience {
  return typeof value === 'string' && (ANNOUNCEMENT_AUDIENCES as readonly string[]).includes(value)
}

export const ANNOUNCEMENT_AUDIENCE_ERROR =
  "audience must be one of 'all', 'members', 'guests'"

/** A string[] of non-empty strings, or null. Used to validate target_plans / target_locales. */
export function isStringArrayOrNull(value: unknown): value is string[] | null {
  return (
    value == null ||
    (Array.isArray(value) && value.every((v) => typeof v === 'string' && v.trim() !== ''))
  )
}

export interface AnnouncementTargetingColumns {
  audience?: string
  target_plans?: string[] | null
  target_locales?: string[] | null
  starts_at?: string | null
  ends_at?: string | null
  severity?: string
  dismissible?: boolean
}

/**
 * Validate + normalize the targeting/scheduling/severity fields from an
 * announcement create or update body. Only keys PRESENT in the body are
 * returned (so it works for both a full create and a partial update). Empty
 * strings / empty arrays normalize to `null` (= "no filter" / "open-ended").
 * Shared by the POST and PUT routes so the server is the authority.
 */
export function parseAnnouncementTargeting(
  body: Record<string, unknown>,
): { error: string } | { columns: AnnouncementTargetingColumns } {
  const columns: AnnouncementTargetingColumns = {}

  if (body.audience !== undefined) {
    if (!isAnnouncementAudience(body.audience)) return { error: ANNOUNCEMENT_AUDIENCE_ERROR }
    columns.audience = body.audience
  }

  if (body.severity !== undefined) {
    if (!isAnnouncementSeverity(body.severity)) return { error: ANNOUNCEMENT_SEVERITY_ERROR }
    columns.severity = body.severity
  }

  for (const key of ['target_plans', 'target_locales'] as const) {
    if (body[key] === undefined) continue
    const v = body[key]
    if (v === null || (Array.isArray(v) && v.length === 0)) {
      columns[key] = null
      continue
    }
    if (!isStringArrayOrNull(v)) {
      return { error: `${key} must be an array of non-empty strings` }
    }
    columns[key] = v as string[]
  }

  for (const key of ['starts_at', 'ends_at'] as const) {
    if (body[key] === undefined) continue
    const v = body[key]
    if (v === null || v === '') {
      columns[key] = null
      continue
    }
    if (typeof v !== 'string' || Number.isNaN(Date.parse(v))) {
      return { error: `${key} must be an ISO date-time or null` }
    }
    columns[key] = new Date(v).toISOString()
  }

  if (body.dismissible !== undefined) {
    if (typeof body.dismissible !== 'boolean') return { error: 'dismissible must be a boolean' }
    columns.dismissible = body.dismissible
  }

  // When both bounds are supplied in the same request, the end must be after
  // the start. (A partial update touching only one bound is left to the admin.)
  if (columns.starts_at && columns.ends_at && Date.parse(columns.ends_at) <= Date.parse(columns.starts_at)) {
    return { error: 'ends_at must be after starts_at' }
  }

  return { columns }
}
